import type { ColumnDef } from '@tanstack/react-table'
import { Check, Clock3, CircleDashed, CircleX } from 'lucide-react'
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import type { DataTableFilterField } from '@/components/data-table/types'
import { TextWithTooltip } from '@/components/custom/text-with-tooltip'
import { field, createSchema } from '@/lib/store/schema'
import { cn } from '@/lib/utils'
import {
  documentDate,
  signingStateLabels,
  type CollectedDocument,
  type SigningState,
} from './model'

export const DOCUMENTS_TABLE_ID = 'signing-documents'
export const documentsSchema = createSchema({
  uuid: field.string().default(null),
  // oxlint-disable-next-line unicorn/no-array-sort -- schema field builder, not Array.prototype.sort
  sort: field.sort().default({ id: 'createdAt', desc: true }),
  memberName: field.string().default(null),
  configurationName: field.string().default(null),
  formKey: field.array(field.string()).delimiter(',').default([]),
  state: field.array(field.string()).delimiter(',').default([]),
  createdAt: field.array(field.timestamp()).delimiter('-').default([]),
})

export const documentsFilterFields: DataTableFilterField<CollectedDocument>[] =
  [
    { label: 'Member', value: 'memberName', type: 'input', defaultOpen: true },
    {
      label: 'Document',
      value: 'formKey',
      type: 'checkbox',
      defaultOpen: true,
      compact: true,
      options: [
        { label: 'MC382', value: 'mc382' },
        { label: 'ABCDM229', value: 'abcdm229' },
      ],
    },
    {
      label: 'Status',
      value: 'state',
      type: 'checkbox',
      defaultOpen: true,
      compact: true,
      options: Object.entries(signingStateLabels).map(([value, label]) => ({
        value,
        label,
      })),
    },
    {
      label: 'Created',
      value: 'createdAt',
      type: 'timerange',
      defaultOpen: false,
    },
    {
      label: 'Configuration',
      value: 'configurationName',
      type: 'input',
      defaultOpen: false,
    },
  ]

export function SigningStatus({ state }: { state: SigningState }) {
  const Icon =
    state === 'signed'
      ? Check
      : state === 'awaiting_signature' || state === 'expired'
        ? Clock3
        : state === 'declined' || state === 'failed' || state === 'voided'
          ? CircleX
          : CircleDashed
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap text-xs',
        state === 'signed'
          ? 'text-emerald-700 dark:text-emerald-400'
          : state === 'awaiting_signature'
            ? 'text-amber-700 dark:text-amber-400'
            : state === 'failed' || state === 'declined'
              ? 'text-red-700 dark:text-red-400'
              : 'text-muted-foreground'
      )}>
      <Icon className="size-3.5 shrink-0" />
      {signingStateLabels[state]}
    </span>
  )
}

export const documentsColumns: ColumnDef<CollectedDocument>[] = [
  {
    accessorKey: 'formKey',
    enableHiding: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Document" />
    ),
    cell: ({ row, table }) => (
      <button
        type="button"
        className="cursor-pointer text-left font-medium hover:underline focus-visible:underline"
        aria-label={`Open ${row.original.formName} for ${row.original.memberName}`}
        onClick={(event) => {
          event.stopPropagation()
          table.setRowSelection({ [row.id]: true })
        }}>
        {row.original.formName}
      </button>
    ),
  },
  {
    accessorKey: 'memberName',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Member" />
    ),
    cell: ({ row }) => <TextWithTooltip text={row.original.memberName} />,
  },
  {
    accessorKey: 'configurationName',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Configuration" />
    ),
    cell: ({ row }) => (
      <TextWithTooltip text={row.original.configurationName} />
    ),
  },
  {
    accessorKey: 'state',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => <SigningStatus state={row.original.state} />,
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: ({ row }) => (
      <span className="whitespace-nowrap">
        {documentDate(row.original.createdAt)}
      </span>
    ),
  },
  {
    accessorKey: 'signedAt',
    sortUndefined: 'last',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Signed" />
    ),
    cell: ({ row }) =>
      row.original.signedAt ? (
        <span className="whitespace-nowrap">
          {documentDate(row.original.signedAt)}
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
]
